import { Collapse } from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { paginationDefaults, langDate } from "../../../../../utils/properties";
import Filter from "../../../../../components/functional_components/Filter/Filter";
import { useFormik } from "formik";
import { get, toLower } from "lodash";
import { buildQueryString, showError, trimData } from "../../../../../utils/utilities";
import InternalRate from "./InternalRate";
import ExternalRate from "./ExternalRate";
import { setLoading } from "../../../../../redux/ducks/Loading";
import Resource from "../../../../../services/api/Resource";
import { useParams } from "react-router-dom";
import ServicesHours from "./ServicesHours";
import ServicesPercentage from "./ServicePercentage";
import { setFilters } from "../../../../../redux/ducks/Filters";
import dayjs from "dayjs";

const ProjectsTeam = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentDate = dayjs().format("YYYY");
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const params = useParams();
  const [state, setState] = useState({
    activePanel: null,
    pagination: {
      currentPage: paginationDefaults.page,
      size: paginationDefaults.size,
      totalItems: paginationDefaults.totalItems,
    },
    loading: false,
    filterLoading: false,
    modal: {
      visible: false,
      type: "",
      data: {},
    },
  });

  useEffect(() => {
    dataList();
  }, [state.activePanel]);

  const formikFilter = useFormik({
    initialValues: {
      year: dayjs(currentDate, "YYYY"),
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.year) errors.year = "Required!";
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      dataList();
      dispatch(setFilters(buildQueryString({}, { year: dayjs(values.year, "YYYY").format("YYYY") })));
    },
  });

  const fieldsFilter = [
    {
      type: "year",
      isRequired: true,
      id: "year",
      name: "year",
      format: "YYYY",
      locale: langDate[`locale_${toLower(currentLanguage)}`],
      label: t("projectEstimate.filter.year"),
      value: formikFilter.values.year,
      onChange: (date) => {
        formikFilter.setFieldValue("year", date);
      },
    },
  ];

  const dataList = async () => {
    setState((prevState) => ({ ...prevState, tableContent: [] }));
    switch (state.activePanel) {
      case "internal":
        internalData();
        break;
      case "external":
        externalData();
        break;
      case "servicesHours":
        servicesHoursData();
        break;
      case "servicesPercentage":
        servicesPercentageData();
        break;
      default:
        break;
    }
  };

  const servicesHoursData = async () => {
    dispatch(setLoading(true));
    const services = await Resource.servicesHourList(params.id, dayjs(formikFilter.values.year, "YYYY").format("YYYY"));
    if (!services.error && services.data) {
      services.data.forEach((client, i) => {
        ((client.key = i), (client.description = `${client.projectTaskId} | ${client.projectTaskName}`));
      });
      setState((prevState) => ({
        ...prevState,
        tableContent: get(services, "data", []),
        pagination: {
          currentPage: services.currentPage ? services.currentPage + 1 : paginationDefaults.page,
          size: get(services, "pageSize", paginationDefaults.size),
          totalItems: get(services, "totalItems", paginationDefaults.totalItems),
          currentPageProjectEstimate: state.pagination.currentPageProjectEstimate,
          totalCurrentProjectEstimate: state.pagination.totalCurrentProjectEstimate,
        },
      }));
    } else {
      showError(get(services, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const servicesPercentageData = async () => {
    dispatch(setLoading(true));
    const services = await Resource.servicesPercentList(
      params.id,
      dayjs(formikFilter.values.year, "YYYY").format("YYYY")
    );
    if (!services.error && services.data) {
      services.data.forEach((client, i) => {
        ((client.key = i), (client.description = `${client.projectTaskId} | ${client.projectTaskName}`));
      });
      setState((prevState) => ({
        ...prevState,
        tableContent: get(services, "data", []),
        pagination: {
          currentPage: services.currentPage ? services.currentPage + 1 : paginationDefaults.page,
          size: get(services, "pageSize", paginationDefaults.size),
          totalItems: get(services, "totalItems", paginationDefaults.totalItems),
          currentPageProjectEstimate: state.pagination.currentPageProjectEstimate,
          totalCurrentProjectEstimate: state.pagination.totalCurrentProjectEstimate,
        },
      }));
    } else {
      showError(get(services, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const internalData = async () => {
    dispatch(setLoading(true));
    const services = await Resource.internalList(params.id, dayjs(formikFilter.values.year, "YYYY").format("YYYY"));
    if (!services.error && services.data) {
      services.data.forEach((client, i) => {
        client.key = i;
      });
      setState((prevState) => ({
        ...prevState,
        tableContent: get(services, "data", []),
        pagination: {
          currentPage: services.currentPage ? services.currentPage + 1 : paginationDefaults.page,
          size: get(services, "pageSize", paginationDefaults.size),
          totalItems: get(services, "totalItems", paginationDefaults.totalItems),
          currentPageProjectEstimate: state.pagination.currentPageProjectEstimate,
          totalCurrentProjectEstimate: state.pagination.totalCurrentProjectEstimate,
        },
      }));
    } else {
      showError(get(services, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const externalData = async () => {
    dispatch(setLoading(true));
    const services = await Resource.externalList(params.id, dayjs(formikFilter.values.year, "YYYY").format("YYYY"));
    if (!services.error && services.data) {
      services.data.forEach((client, i) => {
        client.key = i;
      });
      setState((prevState) => ({
        ...prevState,
        tableContent: get(services, "data", []),
        pagination: {
          currentPage: services.currentPage ? services.currentPage + 1 : paginationDefaults.page,
          size: get(services, "pageSize", paginationDefaults.size),
          totalItems: get(services, "totalItems", paginationDefaults.totalItems),
          currentPageProjectEstimate: state.pagination.currentPageProjectEstimate,
          totalCurrentProjectEstimate: state.pagination.totalCurrentProjectEstimate,
        },
      }));
    } else {
      showError(get(services, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const resetFilter = () => {
    const queryString = buildQueryString({}, { year: currentDate });
    formikFilter.setFieldValue("year", dayjs(queryString.obj.year, "YYYY"));
    dispatch(setFilters(queryString));
    dataList();
  };
  const items = [
    {
      key: "internal",
      label: t("projectsManagement.team.internal"),
      className: "mb-1em",
      children: <InternalRate data={state.tableContent} />,
    },
    {
      key: "external",
      label: t("projectsManagement.team.external"),
      className: "mb-1em",
      children: <ExternalRate data={state.tableContent} />,
    },
    {
      key: "servicesHours",
      label: t("projectsManagement.team.servicesInHours"),
      className: "mb-1em",
      children: <ServicesHours year={2022} data={state.tableContent} />,
    },
    {
      key: "servicesPercentage",
      label: t("projectsManagement.team.servicesPercentage"),
      className: "mb-1em",
      children: <ServicesPercentage year={2022} data={state.tableContent} />,
    },
  ];

  return (
    <>
      <Filter
        formikForm={formikFilter}
        fields={fieldsFilter}
        loading={state.filterLoading}
        allInOneRow={false}
        onReset={resetFilter}
      />
      <Collapse
        items={items}
        accordion
        activeKey={state.activePanel}
        onChange={(key) => setState((prevState) => ({ ...prevState, activePanel: key[0] || null }))}
      />
    </>
  );
};

export default ProjectsTeam;
